import { useTranslation } from 'react-i18next';
import { Navigation, X, Plus } from '../ui/Icon';
import { Icon } from '../ui/Icon';
import { Map as MapIcon } from '../ui/Icon';
import SearchPhotoCarousel from './SearchPhotoCarousel';

export interface SearchResult {
  name: string;
  lat: number;
  lng: number;
  address: string;
  mapsUrl: string;
  placeId: string | null;
  photos?: string[];
}

interface Props {
  searchResult: SearchResult;
  screenPos: { x: number; y: number } | null;
  onClose: () => void;
  onCenter: () => void;
  onSave: () => void;
}

export default function SearchResultCard({ searchResult, screenPos, onClose, onCenter, onSave }: Props) {
  const { t } = useTranslation();

  const CARD_W = Math.min(384, window.innerWidth - 32);
  const GAP = 14;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const MAX_CARD_H = Math.min(Math.round(vh * 0.7), 480);

  let srStyle: React.CSSProperties;
  if (screenPos && screenPos.x > 0 && screenPos.y > 0 && screenPos.x < vw && screenPos.y < vh) {
    const left = Math.max(16, Math.min(vw - CARD_W - 16, screenPos.x - CARD_W / 2));
    const canShowBelow = (screenPos.y + GAP + MAX_CARD_H) <= (vh - 16);
    const canShowAbove = (screenPos.y - GAP - MAX_CARD_H) >= 16;
    const showAbove = !canShowBelow;
    const top = !showAbove
      ? screenPos.y + GAP
      : canShowAbove
        ? screenPos.y - GAP - MAX_CARD_H
        : Math.max(16, Math.round((vh - MAX_CARD_H) / 2));
    srStyle = { position: 'fixed', zIndex: 9999, width: CARD_W, left, top, maxHeight: `${MAX_CARD_H}px`, overflowY: 'auto' };
  } else {
    srStyle = { position: 'fixed', zIndex: 9999, width: CARD_W, bottom: 76, left: '50%', transform: 'translateX(-50%)', maxHeight: `${MAX_CARD_H}px`, overflowY: 'auto' };
  }

  return (
    <div style={srStyle}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {searchResult.photos && searchResult.photos.length > 0 && (
          <SearchPhotoCarousel photos={searchResult.photos} name={searchResult.name} />
        )}
        <div className="p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm truncate">{searchResult.name}</p>
              <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{searchResult.address}</p>
            </div>
            <button onClick={onClose} className="text-slate-300 hover:text-slate-500 shrink-0 cursor-pointer">
              <Icon as={X} size={16} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onCenter}
              className="text-xs font-medium text-slate-500 hover:text-teal-600 flex items-center gap-1 transition-colors cursor-pointer">
              <Icon as={Navigation} size={14} /> {t('map.center')}
            </button>
            <a href={searchResult.mapsUrl} target="_blank" rel="noopener noreferrer"
              className="text-xs font-medium text-slate-500 hover:text-teal-600 flex items-center gap-1 transition-colors">
              <Icon as={MapIcon} size={14} /> Maps
            </a>
            <div className="flex-1" />
            <button onClick={onSave} className="btn-primary gap-1.5 text-xs py-1.5">
              <Icon as={Plus} size={14} /> {t('map.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
